'use client'

import React from 'react'
import { motion } from 'framer-motion'
import { Button } from '@sarkiai/ui'
import { BarChart3, Users, Settings2, ArrowRight } from 'lucide-react'
import Link from 'next/link'
import Image from 'next/image'
import { useTranslations } from '@/components/I18nProvider'

interface EnterpriseDetailProps {
  /** Custom CTA link (defaults to /enterprise) */
  ctaLink?: string
  /** Custom CTA label key (defaults to enterprise.discover) */
  ctaLabelKey?: string
}

export function EnterpriseDetail({ ctaLink = '/enterprise', ctaLabelKey = 'enterprise.discover' }: EnterpriseDetailProps = {}) {
  const t = useTranslations('services')

  const pillars = [
    { icon: BarChart3, title: t('enterprise.pillar1.title'), desc: t('enterprise.pillar1.desc') },
    { icon: Users, title: t('enterprise.pillar2.title'), desc: t('enterprise.pillar2.desc') },
    { icon: Settings2, title: t('enterprise.pillar3.title'), desc: t('enterprise.pillar3.desc') },
  ]

  return (
    <section className="py-20 relative overflow-hidden px-4 lg:px-12">
      <div className="container mx-auto">
        <div className="flex flex-col lg:flex-row items-center gap-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="flex-1 space-y-8"
          >
            <h2 className="text-2xl md:text-4xl uppercase tracking-tighter leading-tight text-foreground">
              {t('enterprise.hero.title')}
            </h2>

            <p className="text-md text-muted-foreground leading-relaxed">
              {t('enterprise.hero.subtitle')}
            </p>

            <div className="space-y-4 pt-4">
              {pillars.map((p, i) => (
                <motion.div
                  key={i}
                  className="flex gap-4 p-4 rounded-2xl bg-card/50 border border-border/50 hover:bg-card/80 transition-colors"
                  whileHover={{ x: 10 }}
                >
                  <div className="shrink-0">
                    <div className="w-14 h-14 rounded-2xl bg-transparent border border-black/10 dark:border-white/20 flex items-center justify-center shadow-lg shadow-black/5">
                      <p.icon className="w-6 h-6 text-black dark:text-white" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <h4 className="uppercase tracking-tight text-xl">{p.title}</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="pt-8">
              <Button asChild size="default" className="rounded-2xl px-8 h-10 text-xs bg-black text-white hover:bg-black/90 uppercase tracking-widest transition-all">
                <Link href={ctaLink}>
                  {t(ctaLabelKey)}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex-1"
          >
            {/* Theme-aware Hero Image */}
            <div className="w-full rounded-3xl overflow-hidden shadow-2xl">
              {/* Light mode image */}
              <Image
                src="/assets/images/hero/hero-land-1-min-light.webp"
                alt="SarkiAI Enterprise"
                width={800}
                height={600}
                className="w-full h-auto dark:hidden rounded-3xl"
              />
              {/* Dark mode image */}
              <Image
                src="/assets/images/hero/hero-land-2-min-dark.webp"
                alt="SarkiAI Enterprise"
                width={800}
                height={600}
                className="w-full h-auto hidden dark:block rounded-3xl"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
